<template>
  <admin-modal size="lg" :title="title" :subtitle="subtitle" :busy="saving" @close="$emit('close')">
    <div class="adm-scope rxe">
      <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>

      <div v-if="loading" class="rxe-loading">
        <div class="adm-skeleton" style="height: 60px"></div>
        <div class="adm-skeleton" style="height: 120px; margin-top: 12px"></div>
      </div>

      <template v-else-if="sheet">
        <div v-if="sheet.appointment.reason" class="rxe-complaint">
          <span class="adm-label">Chief complaint</span>
          {{ sheet.appointment.reason }}
        </div>

        <div class="adm-field">
          <label class="adm-label" for="rxe-note">Diagnosis &amp; notes</label>
          <textarea id="rxe-note" v-model="note" class="adm-textarea" rows="3" maxlength="500" :readonly="readonly"
                    placeholder="e.g. Acute viral fever. Rest, plenty of fluids, review after 3 days if fever persists."></textarea>
          <div class="adm-field__help rxe-count">{{ note.length }}/500</div>
        </div>

        <div class="rxe-medhead">
          <span class="adm-label">Medicines <span class="adm-tab__count">{{ meds.length }}</span></span>
        </div>

        <!-- Add a medicine -->
        <div v-if="!readonly" class="rxe-add">
          <div class="adm-search rxe-search">
            <i class="fas fa-capsules"></i>
            <input ref="search" v-model="query" class="adm-input" autocomplete="off" placeholder="Search your medicines, or type a new name and press Enter"
                   aria-label="Add medicine" @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)" @keydown.enter.prevent="pick()"
                   @keydown.esc="query = ''" />
          </div>
          <div v-if="query.trim()" class="rxe-suggest" role="listbox">
            <button v-for="(m, i) in suggestions" :key="`${m.medicinename}-${m.brandname}-${i}`" type="button" role="option"
                    class="rxe-suggest__item" :class="{ 'is-active': i === active }" @mousedown.prevent="pick(m)">
              <span class="adm-strong">{{ m.medicinename }}</span>
              <span class="adm-muted">{{ [m.brandname, m.dosage].filter(Boolean).join(" · ") }}</span>
            </button>
            <button type="button" class="rxe-suggest__item rxe-suggest__new" :class="{ 'is-active': active === suggestions.length }"
                    @mousedown.prevent="pick(null)">
              <i class="fas fa-plus"></i> Add “{{ query.trim() }}” as a new medicine
            </button>
          </div>
        </div>

        <div v-if="!meds.length" class="adm-empty rxe-empty">
          <i class="fas fa-prescription-bottle-alt"></i>
          <strong>No medicines yet</strong>
          {{ readonly ? "This prescription has no medicines." : "Search above to add the first medicine." }}
        </div>

        <ol class="rxe-list">
          <li v-for="(m, i) in meds" :key="m.key" class="rxe-med">
            <div class="rxe-med__top">
              <span class="rxe-med__n">{{ i + 1 }}</span>
              <div class="rxe-med__name">
                <input v-model="m.medicinename" class="adm-input rxe-inline rxe-inline--strong" maxlength="100" :readonly="readonly" aria-label="Medicine name" />
                <input v-model="m.brandname" class="adm-input rxe-inline" maxlength="100" :readonly="readonly" placeholder="Brand" aria-label="Brand" />
                <input v-model="m.dosage" class="adm-input rxe-inline" maxlength="50" :readonly="readonly" placeholder="Form, strength (Tablet, 500 mg)" aria-label="Dosage" />
              </div>
              <div v-if="!readonly" class="rxe-med__tools">
                <button type="button" class="adm-icon-btn" :disabled="i === 0" title="Move up" aria-label="Move up" @click="moveMed(i, -1)"><i class="fas fa-arrow-up"></i></button>
                <button type="button" class="adm-icon-btn" :disabled="i === meds.length - 1" title="Move down" aria-label="Move down" @click="moveMed(i, 1)"><i class="fas fa-arrow-down"></i></button>
                <button type="button" class="adm-icon-btn rxe-del" title="Remove" aria-label="Remove medicine" @click="meds.splice(i, 1)"><i class="fas fa-trash-alt"></i></button>
              </div>
            </div>

            <div class="rxe-med__grid">
              <div>
                <span class="rxe-mini">When</span>
                <div class="rxe-slots" role="group" aria-label="When to take">
                  <button v-for="(s, si) in SLOTS" :key="s" type="button" class="rxe-slot" :class="{ 'is-on': slotOn(m, si) }"
                          :disabled="readonly" :aria-pressed="slotOn(m, si)" @click="toggleSlot(m, si)">{{ s }}</button>
                  <button type="button" class="rxe-slot" :class="{ 'is-on': m.customFreq }" :disabled="readonly"
                          :aria-pressed="m.customFreq" @click="toggleOther(m)">Other</button>
                </div>
                <input v-if="m.customFreq" v-model="m.frequency" class="adm-input rxe-inline rxe-freq-custom" maxlength="50"
                       :readonly="readonly" placeholder="e.g. SOS, once a week" aria-label="Frequency" />
              </div>
              <div>
                <span class="rxe-mini">Days</span>
                <div class="rxe-days">
                  <input v-model="m.duration" class="adm-input rxe-inline rxe-days__input" maxlength="50" :readonly="readonly" inputmode="numeric" aria-label="Number of days" />
                  <template v-if="!readonly">
                    <button v-for="d in ['3', '5', '7', '10']" :key="d" type="button" class="rxe-pill" :class="{ 'is-on': m.duration === d }" @click="m.duration = d">{{ d }}</button>
                  </template>
                </div>
              </div>
              <div class="rxe-med__note">
                <span class="rxe-mini">Instructions</span>
                <div class="rxe-days">
                  <template v-if="!readonly">
                    <button v-for="t in ['After food', 'Before food']" :key="t" type="button" class="rxe-pill" :class="{ 'is-on': m.note === t }" @click="m.note = m.note === t ? '' : t">{{ t }}</button>
                  </template>
                </div>
                <input v-model="m.note" class="adm-input rxe-inline" maxlength="250" :readonly="readonly" placeholder="Any other instruction" aria-label="Instructions" />
              </div>
            </div>
          </li>
        </ol>
      </template>
    </div>

    <template #footer>
      <span v-if="savedAt" class="adm-muted rxe-saved"><i class="fas fa-check-circle"></i> Saved</span>
      <button type="button" class="adm-btn" :disabled="saving || !sheet || !canOutput" @click="output('pdf')"><i class="fas fa-file-pdf"></i> PDF</button>
      <button type="button" class="adm-btn" :disabled="saving || !sheet || !canOutput" @click="output('print')"><i class="fas fa-print"></i> Print</button>
      <button v-if="!readonly" type="button" class="adm-btn adm-btn--primary" :disabled="saving || loading || !sheet" @click="save()">
        <span v-if="saving" class="adm-spinner"></span> {{ sheet && sheet.prescription ? "Save changes" : "Save prescription" }}
      </button>
    </template>
  </admin-modal>
</template>

<script>
import axios from "axios";
import "../../admin/admin.css";
import AdminModal from "../../admin/components/AdminModal.vue";
import { errorMessage } from "../../admin/admin-utils";
import { downloadPrescription, loadSheet, printPrescription } from "@/services/prescription";

const SLOTS = ["Morning", "Afternoon", "Night"];
let nextKey = 0;

function fromServer(m) {
  const freq = (m.frequency || "").trim();
  return {
    key: ++nextKey,
    prescripmedid: m.prescripmedid || 0,
    medicinename: m.medicinename || "",
    brandname: m.brandname && m.brandname.toLowerCase() !== "n/a" ? m.brandname : "",
    dosage: m.dosage || "",
    frequency: freq,
    // Anything that is not "1-0-1" style keeps a free-text box.
    customFreq: !!freq && !/^\d-\d-\d$/.test(freq),
    duration: (m.duration || "").trim(),
    note: m.note || "",
  };
}

export default {
  name: "PrescriptionEditor",
  components: { AdminModal },
  props: {
    appointmentId: { type: [Number, String], required: true },
    doctorId: { type: [Number, String], default: null },
    readonly: { type: Boolean, default: false },
  },
  emits: ["close", "saved"],
  data() {
    return {
      SLOTS,
      sheet: null,
      loading: true,
      saving: false,
      error: "",
      note: "",
      meds: [],
      catalog: [],
      query: "",
      active: 0,
      savedAt: null,
      dirty: false,
    };
  },
  computed: {
    title() {
      return this.sheet ? `Prescription · ${this.sheet.patient.name}` : "Prescription";
    },
    subtitle() {
      if (!this.sheet) return "";
      const p = this.sheet.patient;
      const bits = [p.age ? `${p.age} yrs` : "", { M: "Male", F: "Female", O: "Other" }[p.gender] || "", `Appointment #${this.sheet.appointment.id}`];
      return bits.filter(Boolean).join(" · ");
    },
    suggestions() {
      const q = this.query.trim().toLowerCase();
      if (!q) return [];
      return this.catalog.filter(m => `${m.medicinename} ${m.brandname}`.toLowerCase().includes(q)).slice(0, 7);
    },
    canOutput() {
      return !!(this.sheet && this.sheet.prescription);
    },
  },
  watch: {
    note() { this.dirty = true; },
    meds: { deep: true, handler() { this.dirty = true; } },
    query() { this.active = 0; },
  },
  methods: {
    slotOn(m, i) {
      return /^\d-\d-\d$/.test(m.frequency) && m.frequency.split("-")[i] !== "0";
    },
    toggleSlot(m, i) {
      const parts = /^\d-\d-\d$/.test(m.frequency) ? m.frequency.split("-") : ["0", "0", "0"];
      parts[i] = parts[i] === "0" ? "1" : "0";
      m.frequency = parts.join("-") === "0-0-0" ? "" : parts.join("-");
      m.customFreq = false;
    },
    toggleOther(m) {
      m.customFreq = !m.customFreq;
      // Switching to free text starts empty rather than showing "1-0-1".
      if (m.customFreq && /^\d-\d-\d$/.test(m.frequency)) m.frequency = "";
    },
    move(step) {
      const max = this.suggestions.length; // last index = "add new"
      this.active = Math.min(max, Math.max(0, this.active + step));
    },
    pick(item) {
      const choice = item !== undefined ? item : this.suggestions[this.active] || null;
      const name = this.query.trim();
      if (!choice && !name) return;
      const base = choice || { medicinename: name, brandname: "", dosage: "", frequency: "", duration: "" };
      this.meds.push(fromServer({ ...base, prescripmedid: 0 }));
      this.query = "";
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
    },
    moveMed(i, step) {
      const [m] = this.meds.splice(i, 1);
      this.meds.splice(i + step, 0, m);
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const [sheet] = await Promise.all([loadSheet(this.appointmentId, { letterhead: false }), this.loadCatalog()]);
        this.applySheet(sheet);
      } catch (error) {
        this.error = errorMessage(error, "Could not load the prescription.");
      } finally {
        this.loading = false;
      }
    },
    applySheet(sheet) {
      this.sheet = sheet;
      this.note = (sheet.prescription && sheet.prescription.note) || "";
      this.meds = sheet.medications.map(fromServer);
      this.$nextTick(() => { this.dirty = false; });
    },
    async loadCatalog() {
      const doctorId = this.doctorId || sessionStorage.getItem("userdbid");
      if (!doctorId || this.readonly) return;
      try {
        const { data } = await axios.get(`/doctors/${doctorId}/medicines`);
        this.catalog = (data || []).map(m => {
          const [freq, days] = (m.defaultdose || "").split(",");
          return {
            medicinename: m.medicinename,
            brandname: m.brandname && m.brandname.toLowerCase() !== "n/a" ? m.brandname : "",
            dosage: [m.medicinetype, m.medstrength].filter(Boolean).join(", "),
            frequency: (freq || "").trim(),
            duration: (days || "5").trim(),
          };
        });
      } catch (error) {
        this.catalog = []; // free-text entry still works
      }
    },
    validate() {
      const unnamed = this.meds.findIndex(m => !m.medicinename.trim());
      if (unnamed !== -1) return `Medicine ${unnamed + 1} needs a name.`;
      if (!this.meds.length && !this.note.trim()) return "Add a diagnosis note or at least one medicine.";
      return "";
    },
    async save({ quiet = false } = {}) {
      this.error = this.validate();
      if (this.error) return false;
      this.saving = true;
      try {
        const { data } = await axios.put(`/prescription-sheets/${this.appointmentId}`, {
          note: this.note.trim() || null,
          medications: this.meds.map(m => ({
            prescripmedid: m.prescripmedid,
            medicinename: m.medicinename.trim(),
            brandname: m.brandname.trim() || null,
            dosage: m.dosage.trim() || null,
            frequency: (m.frequency || "").trim() || null,
            duration: String(m.duration || "").trim() || null,
            note: m.note.trim() || null,
          })),
        });
        this.applySheet(data);
        this.savedAt = Date.now();
        if (!quiet) this.$emit("saved", data);
        return true;
      } catch (error) {
        this.error = errorMessage(error, "Could not save the prescription.");
        return false;
      } finally {
        this.saving = false;
      }
    },
    async output(kind) {
      // Unsaved edits are saved first so the paper matches the screen.
      if (!this.readonly && this.dirty && !(await this.save({ quiet: true }))) return;
      try {
        const sheet = await loadSheet(this.appointmentId); // with letterhead images
        if (kind === "print" && printPrescription(sheet)) return;
        await downloadPrescription(sheet);
      } catch (error) {
        this.error = errorMessage(error, "Could not create the prescription document.");
      }
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.rxe {
  font-size: 14px;
}

.rxe-complaint {
  background: #f9fafb;
  border: 1px solid var(--adm-line);
  border-radius: var(--adm-radius-sm);
  padding: 10px 12px;
  margin-bottom: 14px;
}

.rxe-complaint .adm-label {
  margin-bottom: 2px;
}

.rxe-count {
  text-align: right;
}

.rxe-medhead {
  margin-bottom: 8px;
}

.rxe-add {
  position: relative;
  margin-bottom: 12px;
}

.rxe-search {
  max-width: none;
}

.rxe-suggest {
  position: absolute;
  z-index: 5;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid var(--adm-line);
  border-radius: var(--adm-radius-sm);
  box-shadow: var(--adm-shadow-lg);
  overflow: hidden;
}

.rxe-suggest__item {
  display: flex;
  gap: 10px;
  align-items: baseline;
  width: 100%;
  text-align: left;
  border: 0;
  background: #fff;
  padding: 9px 12px;
  cursor: pointer;
  font-size: 13px;
}

.rxe-suggest__item.is-active,
.rxe-suggest__item:hover {
  background: var(--adm-primary-50);
}

.rxe-suggest__new {
  color: var(--adm-primary-600);
  font-weight: 600;
  border-top: 1px solid var(--adm-line-2);
}

.rxe-empty {
  padding: 24px;
}

.rxe-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

.rxe-med {
  border: 1px solid var(--adm-line);
  border-radius: var(--adm-radius-sm);
  padding: 12px;
}

.rxe-med__top {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.rxe-med__n {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--adm-primary-50);
  color: var(--adm-primary-600);
  font-weight: 700;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 6px;
}

.rxe-med__name {
  flex: 1;
  display: grid;
  grid-template-columns: 1.3fr 1fr 1.2fr;
  gap: 6px;
}

.rxe-med__tools {
  display: flex;
}

.rxe-del:hover:not(:disabled) {
  color: var(--adm-danger-700);
  background: var(--adm-danger-50);
}

.rxe-inline {
  padding: 6px 9px;
  font-size: 13px;
}

.rxe-inline--strong {
  font-weight: 700;
}

.rxe-inline[readonly] {
  background: #f9fafb;
}

.rxe-med__grid {
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 14px;
  margin: 10px 0 0 34px;
}

.rxe-mini {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--adm-muted);
  margin-bottom: 4px;
}

.rxe-slots,
.rxe-days {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.rxe-slot,
.rxe-pill {
  border: 1px solid var(--adm-line);
  background: #fff;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--adm-ink-2);
  cursor: pointer;
}

.rxe-slot.is-on,
.rxe-pill.is-on {
  background: var(--adm-primary);
  border-color: var(--adm-primary);
  color: #fff;
}

.rxe-slot:disabled {
  cursor: default;
}

.rxe-freq-custom {
  margin-top: 6px;
}

.rxe-days__input {
  width: 56px;
}

.rxe-med__note .adm-input {
  margin-top: 6px;
}

.rxe-saved {
  margin-right: auto;
  align-self: center;
  font-size: 13px;
}

.rxe-saved i {
  color: var(--adm-success);
}

@media (max-width: 720px) {
  .rxe-med__name,
  .rxe-med__grid {
    grid-template-columns: 1fr;
  }

  .rxe-med__grid {
    margin-left: 0;
  }
}
</style>
